import { Box, Stack, Tab, Tabs } from "@mui/material";
import { PREVIEW_ICON_GAP } from "components/PreviewFeature/constants";
import { PreviewFeatureIndicator } from "components/PreviewFeature/PreviewFeatureIndicator";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useCallback } from "react";
import { Navigate, Outlet, useLocation, useMatch, useNavigate } from "react-router-dom";

function getCurrentView(pathname: string): string {
  if (pathname.includes("/site-access")) return "site-access";
  if (pathname.includes("/steers")) return "steers";
  if (pathname.includes("/rcra-info")) return "rcra-info";
  if (pathname.includes("/epcra")) return "epcra";
  // Falls through to the same tab the bare `/staff/credentials` redirect lands on,
  // so the highlighted tab and the default destination can't disagree.
  return "manage";
}

export function CredentialsDashboard() {
  const navigate = useNavigate();
  const location = useLocation();

  const epcraMatch = useMatch("/staff/credentials/epcra/:credentialImplementationId?");
  const rcraMatch = useMatch("/staff/credentials/rcra-info");
  const steersMatch = useMatch("/staff/credentials/steers");
  const siteAccessMatch = useMatch("/staff/credentials/site-access");
  const manageMatch = useMatch("/staff/credentials/manage");
  const isOverview = epcraMatch ?? rcraMatch ?? steersMatch ?? siteAccessMatch ?? manageMatch;

  useBreadcrumb({
    label: "Credentials",
    to: "/staff/credentials",
  });

  const currentView = getCurrentView(location.pathname);

  const handleViewChange = useCallback(
    (_event: React.SyntheticEvent, newView: string) => {
      navigate(`/staff/credentials/${newView}`);
    },
    [navigate]
  );

  if (!isOverview && location.pathname === "/staff/credentials") {
    return <Navigate to="/staff/credentials/manage" replace />;
  }

  return (
    <Box>
      {isOverview && (
        <Stack
          sx={{
            backgroundColor: "background.paper",
            borderBottom: "1px solid",
            borderBottomColor: "divider",
          }}
        >
          {/* Scrollable with auto scroll buttons, matching the shared TabLinks
              behavior other staff pages use: these labels are long enough that
              five of them get cut off at narrower widths. */}
          <Tabs
            value={currentView}
            onChange={handleViewChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              "& .MuiTab-root": {
                color: "text.secondary",
                px: 3,
                mr: 3,
                "&.Mui-selected": {
                  color: "primary.main",
                },
              },
            }}
          >
            <Tab
              value="manage"
              label={
                /* Unconditional rather than `StaffInDevelopmentIcon`: that one keys off a
                   feature flag being unset for the current tenant, and a staff-only route
                   has neither. Same Handyman icon so it reads as the marker used on
                   Manifests and Waste Streams. */
                <Stack direction="row" alignItems="center" spacing={PREVIEW_ICON_GAP}>
                  <span>Credential Management</span>
                  <PreviewFeatureIndicator
                    inline
                    color="inherit"
                    title="This page is in development and not yet ready for general use."
                  />
                </Stack>
              }
            />
            <Tab value="epcra" label="EPCRA Credential Implementation" />
            <Tab value="rcra-info" label="RCRAInfo Credentials" />
            <Tab value="site-access" label="RCRAInfo Site Access" />
            <Tab value="steers" label="STEERS Credentials" />
          </Tabs>
        </Stack>
      )}
      <Outlet />
    </Box>
  );
}
